import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { INDUSTRIES, SOLUTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoModal = ({ open, onClose }) => {
    const [form, setForm] = useState({
        name: "",
        work_email: "",
        phone: "",
        company: "",
        industry: "",
        use_case: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (open) {
            setSubmitted(false);
            setForm({ name: "", work_email: "", phone: "", company: "", industry: "", use_case: "", message: "" });
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        if (open) document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.work_email || !form.company) {
            toast.error("Please fill in your name, work email, and company.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                ...form,
                source_page: window.location.pathname,
            });
            setSubmitted(true);
            toast.success("Request received. A specialist will reach out shortly.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Could not submit request.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-slate-200 text-slate-900 placeholder-slate-400 px-4 py-3 rounded-xl text-sm outline-none focus:border-slate-900 transition-colors";

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="demo-modal"
                >
                    <motion.div
                        className="absolute inset-0"
                        style={{
                            background: "rgba(26, 24, 23, 0.45)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                        }}
                        onClick={onClose}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />
                    <motion.div
                        className="relative w-full max-w-2xl bg-white p-6 sm:p-8 md:p-10 max-h-[92vh] overflow-y-auto"
                        style={{
                            borderRadius: 24,
                            border: "1px solid #eeeae2",
                            boxShadow: "0 40px 100px rgba(80, 60, 45, 0.20)",
                        }}
                        initial={{ y: 30, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0, scale: 0.98 }}
                        transition={{ type: "spring", damping: 22, stiffness: 260 }}
                    >
                        <button
                            aria-label="Close"
                            onClick={onClose}
                            className="absolute right-4 top-4 w-9 h-9 grid place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-all"
                            data-testid="demo-modal-close"
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>

                        {!submitted ? (
                            <>
                                <div className="text-sm text-slate-500 mb-3">Book a demo</div>
                                <h2 className="heading-hero text-3xl md:text-4xl">
                                    See AI augmentation in
                                    <br />
                                    <span className="muted">your operations.</span>
                                </h2>
                                <p className="text-slate-600 mt-4 text-sm md:text-base">
                                    Bring us a workflow. We&apos;ll show you where AI can support better decisions and
                                    faster execution — securely, inside your guardrails.
                                </p>

                                <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field label="Full name" required>
                                        <input
                                            className={inputCls}
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Priya Sharma"
                                            data-testid="demo-input-name"
                                        />
                                    </Field>
                                    <Field label="Work email" required>
                                        <input
                                            type="email"
                                            className={inputCls}
                                            value={form.work_email}
                                            onChange={set("work_email")}
                                            placeholder="priya@company.com"
                                            data-testid="demo-input-email"
                                        />
                                    </Field>
                                    <Field label="Phone number">
                                        <input
                                            type="tel"
                                            className={inputCls}
                                            value={form.phone}
                                            onChange={set("phone")}
                                            placeholder="+91 98765 43210"
                                            data-testid="demo-input-phone"
                                        />
                                    </Field>
                                    <Field label="Company" required>
                                        <input
                                            className={inputCls}
                                            value={form.company}
                                            onChange={set("company")}
                                            placeholder="Company Ltd."
                                            data-testid="demo-input-company"
                                        />
                                    </Field>
                                    <Field label="Industry">
                                        <select
                                            className={inputCls}
                                            value={form.industry}
                                            onChange={set("industry")}
                                            data-testid="demo-select-industry"
                                        >
                                            <option value="">Select…</option>
                                            {INDUSTRIES.map((i) => (
                                                <option key={i.key} value={i.name}>
                                                    {i.name}
                                                </option>
                                            ))}
                                            <option value="Other">Other</option>
                                        </select>
                                    </Field>
                                    <Field label="Interested in" className="md:col-span-2">
                                        <select
                                            className={inputCls}
                                            value={form.use_case}
                                            onChange={set("use_case")}
                                            data-testid="demo-select-usecase"
                                        >
                                            <option value="">Any solution</option>
                                            {SOLUTIONS.map((s) => (
                                                <option key={s.slug} value={s.title}>
                                                    {s.title}
                                                </option>
                                            ))}
                                            <option value="Platform overview">Platform overview</option>
                                        </select>
                                    </Field>
                                    <Field
                                        label="Tell us the workflow you'd like to augment"
                                        className="md:col-span-2"
                                    >
                                        <textarea
                                            rows={3}
                                            className={inputCls}
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="e.g., Claims triage across email and CRM; response drafting; policy checks."
                                            data-testid="demo-input-message"
                                        />
                                    </Field>

                                    <div className="md:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
                                        <p className="text-xs text-slate-500">
                                            Your data stays inside NyraAI. We reply within one business day.
                                        </p>
                                        <button
                                            type="submit"
                                            className="pill-dark"
                                            disabled={submitting}
                                            data-testid="demo-submit"
                                        >
                                            {submitting ? (
                                                <>
                                                    <i className="fa-solid fa-circle-notch fa-spin" /> Sending…
                                                </>
                                            ) : (
                                                <>
                                                    Request demo{" "}
                                                    <i className="fa-solid fa-arrow-right text-xs" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-6" data-testid="demo-success">
                                <div className="w-16 h-16 mx-auto rounded-full grid place-items-center bg-slate-900 text-white">
                                    <i className="fa-solid fa-check text-2xl" />
                                </div>
                                <h3 className="heading-section text-2xl md:text-3xl mt-6">
                                    Received. We&apos;ll look at your workflow.
                                </h3>
                                <p className="text-slate-600 mt-3 text-sm">
                                    A NyraAI specialist will reach out with the shortest path to augmenting
                                    your operations.
                                </p>
                                <button
                                    onClick={onClose}
                                    className="pill-ghost mt-6"
                                    data-testid="demo-success-close"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Field = ({ label, children, required, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="block text-xs uppercase tracking-[0.15em] text-slate-500 mb-2">
            {label} {required && <span className="text-slate-900">*</span>}
        </span>
        {children}
    </label>
);

export default DemoModal;
